import { useEffect, useRef } from "react";

/**
 * Хук для обработки клавиатурной навигации
 * @param {Object} options - Настройки навигации
 * @param {Function} options.onGoLeft - Callback для движения влево/назад
 * @param {Function} options.onGoRight - Callback для движения вправо/вперед
 * @param {Array} options.leftKeys - Массив клавиш для движения влево (по умолчанию: ArrowLeft, PageUp)
 * @param {Array} options.rightKeys - Массив клавиш для движения вправо (по умолчанию: ArrowRight, PageDown, Space)
 * @param {boolean} options.preventDefaultOnMatch - Предотвращать ли поведение по умолчанию для обработанных клавиш
 */
export default function useKeyboardNavigation({
  onGoLeft,
  onGoRight,
  leftKeys = ["ArrowLeft", "PageUp"],
  rightKeys = ["ArrowRight", "PageDown", " "],
  preventDefaultOnMatch = true,
} = {}) {
  // Используем refs для хранения актуальных callbacks
  // Это предотвращает переподписку на события при каждом изменении callbacks
  const onGoLeftRef = useRef(onGoLeft);
  const onGoRightRef = useRef(onGoRight);

  // Обновляем refs при изменении callbacks
  useEffect(() => {
    onGoLeftRef.current = onGoLeft;
  }, [onGoLeft]);

  useEffect(() => {
    onGoRightRef.current = onGoRight;
  }, [onGoRight]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Не обрабатываем клавиши, если фокус в элементе ввода
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable);

      if (isInputFocused) return;

      // Проверяем клавиши для движения влево
      if (leftKeys.includes(event.key) && onGoLeftRef.current) {
        if (preventDefaultOnMatch) {
          event.preventDefault();
        }
        onGoLeftRef.current();
        return;
      }

      // Проверяем клавиши для движения вправо
      if (rightKeys.includes(event.key) && onGoRightRef.current) {
        if (preventDefaultOnMatch) {
          event.preventDefault();
        }
        onGoRightRef.current();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [leftKeys, rightKeys, preventDefaultOnMatch]);
  // Callbacks больше не в зависимостях! Используем refs вместо прямых ссылок
}
